import * as Sentry from '@sentry/react';
import { logger } from '../utils/logger';
import { buildProxyUrl, shouldUseDirectAnalytics } from '../utils/urlUtils';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development';
const isProduction = import.meta.env.PROD;

// Custom sampling function for transactions
const shouldTraceTransaction = (context: any): number => {
  try {
    const trackingLevel = getTrackingLevel();
    
    // If user has full tracking level, always trace
    if (trackingLevel === 'full') {
      logger.info('Full tracking level detected, enabling 100% transaction tracing');
      return 1.0;
    }
    
    // If user has detailed tracking level, use higher rate
    if (trackingLevel === 'detailed') {
      logger.info('Detailed tracking level detected, enabling higher transaction tracing');
      return isProduction ? 0.1 : 0.3;
    }
    
    // Otherwise use default rate
    return isProduction ? 0.03 : 0.1;
  } catch (error) {
    logger.error('Error in shouldTraceTransaction:', error);
    return isProduction ? 0.03 : 0.1;
  }
};

// Custom sampling function for session replays
const shouldRecordReplay = (context: any): number => {
  try {
    const trackingLevel = getTrackingLevel();
    
    // If user has full tracking level, always record
    if (trackingLevel === 'full') {
      logger.info('Full tracking level detected, enabling 100% session replay');
      return 1.0;
    }
    
    // If user has detailed tracking level, use higher rate
    if (trackingLevel === 'detailed') {
      logger.info('Detailed tracking level detected, enabling 50% session replay');
      return isProduction ? 0.5 : 0.7;
    }
    
    // Otherwise use default rate - increase slightly for better testing
    return isProduction ? 0.05 : 0.1;
  } catch (error) {
    logger.error('Error in shouldRecordReplay:', error);
    return isProduction ? 0.05 : 0.1;
  }
};

// Helper function to get tracking level
const getTrackingLevel = (): string | null => {
  try {
    const storedConfig = sessionStorage.getItem('sentry_tracking_config');
    if (storedConfig) {
      const config = JSON.parse(storedConfig);
      return config?.trackingLevel || null;
    }
    return sessionStorage.getItem('sentry_tracking_level');
  } catch (error) {
    logger.error('Error getting tracking level:', error);
    return null;
  }
};

// Singleton pattern for Sentry initialization
class SentrySingleton {
  private static instance: SentrySingleton;
  private initialized: boolean = false;
  private initializationPromise: Promise<void> | null = null;

  private constructor() {}

  public static getInstance(): SentrySingleton {
    if (!SentrySingleton.instance) {
      SentrySingleton.instance = new SentrySingleton();
    }
    return SentrySingleton.instance;
  }

  public async initialize(): Promise<void> {
    // If already initialized, return immediately
    if (this.initialized) {
      return Promise.resolve();
    }

    // If initialization is in progress, return the existing promise
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    // Start initialization
    this.initializationPromise = this.performInitialization();
    return this.initializationPromise;
  }

  private async performInitialization(): Promise<void> {
    if (!SENTRY_DSN) {
      logger.info('Sentry DSN not provided, skipping Sentry initialization');
      return;
    }

    const retries = 2;
    for (let i = 0; i < retries; i++) {
      try {
        const config: any = {
          dsn: SENTRY_DSN,
          environment: environment,
          release: `parachute-web@${import.meta.env.VITE_VERSION || '1.0.0'}`,
          tracesSampler: shouldTraceTransaction,
          integrations: [
            Sentry.feedbackIntegration({
              colorScheme: 'system',
              autoInject: false,
            }),
            Sentry.replayIntegration({
              maskAllText: true,
              blockAllMedia: true,
            }),
            Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
          ],
          enableLogs: true,
          tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
          replaysSessionSampleRate: shouldRecordReplay({}),
          replaysOnErrorSampleRate: 1,
          tracePropagationTargets: [],
          beforeSend: this.createBeforeSendHandler(),
          maxBreadcrumbs: 30
        };

        
        if (i === 0 && !shouldUseDirectAnalytics()) {
          config.tunnel = buildProxyUrl('sentry');
          logger.debug('Trying Sentry initialization with proxy');
        } else {
          // Fallback: try without proxy (or if on localhost)
          const reason = shouldUseDirectAnalytics() ? 'on localhost' : 'as fallback';
          logger.warn(`Trying Sentry initialization without proxy ${reason}`);
        }

        Sentry.init(config);
        this.initialized = true;
        logger.info(`Sentry initialized successfully ${i === 0 ? 'with proxy' : 'without proxy (fallback)'}`);
        return;
      } catch (error) {
        logger.error(`Sentry initialization attempt ${i + 1} failed:`, error);
        if (i === retries - 1) {
          logger.error('All retries failed for Sentry initialization. Sentry will be disabled.');
          throw error;
        }
      }
    }
  }

  private createBeforeSendHandler() {
    return (event: any, hint: any) => {
      try {
        // Try to add user context if it's missing but available in session storage
        if (!event.user || !event.user.id) {
          try {
            const storedConfig = sessionStorage.getItem('sentry_tracking_config');
            if (storedConfig) {
              const config = JSON.parse(storedConfig);
              if (config && config.isTracked && config.userId) {
                event.user = {
                  id: config.userId,
                  username: config.entityName || undefined,
                };
                // Also set tags if not present
                if (!event.tags) event.tags = {};
                if (!event.tags.user_id) {
                  event.tags.user_id = config.userId;
                }
                if (config.accountId && !event.tags.account_id) {
                  event.tags.account_id = config.accountId;
                }
                if (config.accountName && !event.tags.account_name) {
                  event.tags.account_name = config.accountName;
                }
                if (config.schoolId && !event.tags.school_id) {
                  event.tags.school_id = config.schoolId;
                }
                if (config.schoolName && !event.tags.school_name) {
                  event.tags.school_name = config.schoolName;
                }
              }
            }
          } catch (storageError) {
            // Ignore storage errors, continue with original event
          }
        }

        // Filter out noisy/expected errors
        const errorMessage = event.message || event.exception?.values?.[0]?.value || '';
        
        // List of specific error patterns to filter out
        const errorFilters = [
          // Specific GraphQL errors that are expected/not actionable
          'GraphQL Error: This survey already expired',
          'GraphQL Error: Error while converting text to audio',
          'GraphQL Error: SSO_AUTH_FAILED',
          'Error while converting text to audio.',
          'This survey already expired.',
          'convertTextToAudio',
          
          // Specific activity/tracking errors that are not critical
          '[Activity] Most active tab check',
          '[Activity] Force check running (leader tab)',
          'Most active tab check',
          'Force check running (leader tab)',
          'Force check running',
          
          // Specific token revalidation errors (expected behavior)
          '[Token Revalidation] Temporarily disabled to prevent re-render loop',
          'Token Revalidation',
          'Temporarily disabled to prevent re-render loop',
          
          // Specific media/video player expected errors
          'The play() request was interrupted by a new load request',
          'Failed to execute \'exitFullscreen\' on \'Document\': Document not active',
          'exitFullscreen',
          
          // Specific database/promise rejection expected errors
          'Non-Error promise rejection captured with value: Object Not Found Matching Id',
          
          // Specific UI/modal expected errors
          '[InactivityModal] Countdown:',
          'seconds remaining',
          
          // Browser extension errors (these are safe to filter broadly)
          'Extension context invalidated',
          'chrome-extension://',
          'moz-extension://',
          
          // Other specific non-critical errors
          'ResizeObserver loop limit exceeded',
        ];
        
        // Check if error message contains any filtered patterns
        const shouldFilter = errorFilters.some(pattern => 
          errorMessage.toLowerCase().includes(pattern.toLowerCase())
        );
        
        if (shouldFilter) {
          // Don't send filtered errors to Sentry
          return null;
        }
        
        // Filter out errors from specific URLs/paths
        const filteredPaths = [
          '/ff/', // Feedback form paths
          'convertTextToAudio',
          'most-active-tab'
        ];
        
        if (filteredPaths.some(path => event.request?.url?.includes(path))) {
          return null;
        }
        
        // Log the event for debugging (only in development)
        if (import.meta.env.DEV) {
          logger.info('Sentry event processed by beforeSend:', event.event_id);
        }
      } catch (error) {
        logger.error('Error in beforeSend:', error);
        // If there's an error in filtering, still send the original event
        return event;
      }
      return event;
    };
  }

  public isInitialized(): boolean {
    return this.initialized;
  }

  public getSentry() {
    return Sentry;
  }
}

export default SentrySingleton;